import React, { useState, useContext, useEffect } from 'react';
import { Paper, Typography, IconButton } from '@mui/material';
import Grid from '@mui/material/Grid2';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CustomOrderArrow from '../common/IconosOrdenacion';
import { useNavigate, useLocation } from 'react-router-dom';
import AppContext from '../../components/AppContext'; 
import { formatEuro } from '../../functions/formatNumber';
import { getColorForAmount } from '../../functions/colors';
import monthName from '../../functions/month';


const MonthlyBalanceListAnual = ({ months, defaultViewMode = 'estalvi' }) => {

  const [sortConfig, setSortConfig] = useState({ key: 'mes', direction: 'desc' });
  const [viewMode, setViewMode] = useState(defaultViewMode);

  useEffect(() => {
    setViewMode(defaultViewMode);
  }, [defaultViewMode]);

  const { ctxApp } = useContext(AppContext);

  const navigate = useNavigate();
  const location = useLocation();

  const listMonths = Object.values(months);

  // Función para manejar la ordenación
  const handleSort = (event, key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    event.stopPropagation();
  };

  const handleToggleViewMode = (e) => {
    setViewMode(prev => {
      if (prev === 'estalvi') return 'inversiones';
      if (prev === 'inversiones') return 'actual';
      return 'estalvi';
    });
    e.stopPropagation();
  };

  const gotoDetail= (mes) => {
    ctxApp.dashboardMensualTabSelected = undefined;
    navigate(`mes/${mes}${location.search || ''}`);
  }

  // Precalcula el saldo acumulado en orden cronológico (meses ascendentes)
  const sortedMonthsAsc = [...listMonths].sort((a, b) => a.mes - b.mes);
  let runningAccum = 0;
  const accumMap = {};
  sortedMonthsAsc.forEach(m => {
    let val = 0;
    if (viewMode === 'estalvi') {
      val = m.importAgregat.saldoEstalvi;
    } else if (viewMode === 'inversiones') {
      val = m.importAgregat.inversions;
    } else {
      val = m.importAgregat.saldo;
    }
    runningAccum += val;
    accumMap[m.mes] = runningAccum;
  });

  // Función para ordenar los balances según el criterio
  const sortedBalances = [...listMonths].sort((a, b) => {
    // Comprobar la clave de ordenación actual
    switch (sortConfig.key) {
      case 'mes':
        // Ordenar por el número de mes (mes.numero)
        return sortConfig.direction === 'asc'
          ? a.mes - b.mes
          : b.mes - a.mes;

      case 'numMoviments':
        // Ordenar por el número de movimientos (movimientos.num)
        return sortConfig.direction === 'asc'
          ? a.importAgregat.numMoviments - b.importAgregat.numMoviments
          : b.importAgregat.numMoviments - a.importAgregat.numMoviments;

      case 'ingressos':
        // Ordenar por ingresos (importes.ingresos)
        return sortConfig.direction === 'asc'
          ? a.importAgregat.ingressos - b.importAgregat.ingressos
          : b.importAgregat.ingressos - a.importAgregat.ingressos;

      case 'despeses':
        // Ordenar por gastos (importes.gastos)
        return sortConfig.direction === 'asc'
          ? a.importAgregat.despeses - b.importAgregat.despeses
          : b.importAgregat.despeses - a.importAgregat.despeses;

      case 'saldo':
        // Ordenar por saldo del mes según el modo
        const getSaldoVal = (item) => {
          if (viewMode === 'estalvi') return item.importAgregat.saldoEstalvi;
          if (viewMode === 'inversiones') return item.importAgregat.inversions;
          return item.importAgregat.saldo;
        };
        return sortConfig.direction === 'asc'
          ? getSaldoVal(a) - getSaldoVal(b)
          : getSaldoVal(b) - getSaldoVal(a);

      case 'saldoAcum':
        // Ordenar por saldo acumulado calculado
        return sortConfig.direction === 'asc'
          ? accumMap[a.mes] - accumMap[b.mes]
          : accumMap[b.mes] - accumMap[a.mes];

      default:
        return 0; // Si no hay un criterio de ordenación válido
  }
  });
  
  return (
    <Paper sx={{ padding: '20px' }}>
        {/* Cabecera del Grid */}
        <Grid container spacing={2} alignItems="center" sx={{ 
          marginBottom: 2, 
          fontWeight: 'bold', 
          backgroundColor: '#f0f0f0', // Fondo claro
          color: '#000', // Texto oscuro
          paddingTop: '2px', 
          paddingBottom: '2px',
          paddingLeft: '10px',
          paddingRight: '10px',
          borderBottom: '2px solid #ddd' // Borde inferior
        }}>
          <Grid size={{xs: 12, sm: 4 }}>
            <Typography variant="body2">
              <span onClick={(e) => handleSort(e, 'mes')}>
                Mes {sortConfig.key === 'mes' && <CustomOrderArrow direction={sortConfig.direction}/> }
              </span>{' '}
              <span style={{ fontSize: '0.8rem', color: 'gray' }} onClick={(e) => handleSort(e, 'numMoviments')}>
                (Num.Moviments) {sortConfig.key === 'numMoviments' && <CustomOrderArrow direction={sortConfig.direction}/> }
              </span>
            </Typography>
          </Grid>
          <Grid size={{xs: 12, sm: 4 }} container direction="column" alignItems="center">
            {viewMode !== 'inversiones' && (
              <Typography variant="body2">
                <span onClick={(e) => handleSort(e, 'ingressos')}>
                  Ingressos {sortConfig.key === 'ingressos' && <CustomOrderArrow direction={sortConfig.direction}/> }
                </span>{' / '}
                <span onClick={(e) => handleSort(e, 'despeses')}>
                  Despeses {sortConfig.key === 'despeses' && <CustomOrderArrow direction={sortConfig.direction}/> }
                </span>
              </Typography>
            )}
          </Grid>
          <Grid size={{xs: 12, sm: 4 }} container direction="row" alignItems="center" justifyContent="flex-end" 
            sx={{ gap: '1px' }}>
            <IconButton onClick={handleToggleViewMode} size="small" title="Canviar vista de saldo" sx={{ marginRight: '5px' }}>
              <SwapHorizIcon sx={{ fontSize: '18px' }} />
            </IconButton>
            <Grid container direction="column" alignItems="flex-end">
              <Typography variant="body2" onClick={(e) => handleSort(e, 'saldo')}>
                {viewMode === 'estalvi' ? "Saldo Estalvi" : viewMode === 'inversiones' ? "Inversions" : "Saldo del Mes"} {sortConfig.key === 'saldo' && <CustomOrderArrow direction={sortConfig.direction}/> }
              </Typography>
              <Typography variant="caption" color="gray" onClick={(e) => handleSort(e, 'saldoAcum')}>
                  Acumulat Anual {sortConfig.key === 'saldoAcum' && <CustomOrderArrow direction={sortConfig.direction}/> }
              </Typography>
            </Grid>
          </Grid>
        </Grid>    
        
        {/* List Mesos */}
        { sortedBalances.map((value) =>  {
          const saldoVal = viewMode === 'estalvi' 
            ? value.importAgregat.saldoEstalvi 
            : viewMode === 'inversiones' 
              ? value.importAgregat.inversions 
              : value.importAgregat.saldo;

          const accumVal = accumMap[value.mes];

          const colorSaldo = getColorForAmount(saldoVal, viewMode === 'inversiones');
          const colorAcum = getColorForAmount(accumVal, viewMode === 'inversiones');

          return (
            <Grid container 
              key={value.mes} 
              spacing={2} 
              alignItems="center" 
              onClick={()=>gotoDetail(value.mes)}
              sx={{ 
                marginBottom: 1,
                paddingLeft: '10px',
                paddingRight: '10px',
                transition: 'background-color 0.3s',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.1)', // Cambia el color de fondo al pasar el ratón
                },
            }}>

              {/* Columna izquierda: Número del mes, nombre y número de movimientos */}
              <Grid size={{xs: 12, sm: 4 }}>
                <Typography variant="body1">
                  <span >{value.mes}. {monthName(value.mes)}</span>{' '}
                  <span style={{ fontSize: '0.8rem', color: 'gray' }}>
                  ({value.importAgregat.numMoviments})
                  </span>
                </Typography>
              </Grid>

              {/* Columna central: Importe ingresado y gastado */}
              <Grid size={{xs: 12, sm: 4 }} container direction="column" alignItems="center">
                {viewMode !== 'inversiones' ? (
                  <Typography variant="body2">
                    <span style={{ color: 'green' }}>
                      {formatEuro(value.importAgregat.ingressos)}
                    </span>{' / '}
                    <span style={{ color: 'red' }}>
                      {formatEuro(value.importAgregat.despeses)}
                    </span>
                  </Typography>
                ) : (
                  <Typography variant="body2" color="gray" sx={{ fontStyle: 'italic' }}>
                    -
                  </Typography>
                )}            
              </Grid>

              {/* Columna derecha: Saldo del mes y saldo acumulado */}
              <Grid size={{xs: 12, sm: 4 }} container direction="column" alignItems="flex-end" 
                sx={{ gap: '1px' }}>
                <Typography variant="body1" fontWeight="bold" color={colorSaldo} >
                  {formatEuro(saldoVal)}
                </Typography>
                
                <Typography variant="caption" color={colorAcum}>
                  {formatEuro(accumVal)}
                </Typography>
              </Grid>
            </Grid>
          );
        }
      )}
    </Paper>
    );
};

export default MonthlyBalanceListAnual;
